import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrainFront, MapPin, Search, Building2, Zap, Mountain, Globe2, Flag, ArrowRight, BedDouble } from "lucide-react";
import { PageHeader, Spinner } from "../components/common";
import { GuideTeaser } from "../components/ArticleSections";
import { GUIDES } from "../content/guides";
import { SITE } from "../lib/site";
import api from "../lib/api";

const TABS = [
  { id: "stations", label: "Stations", icon: <MapPin className="w-4 h-4" /> },
  { id: "operators", label: "Operators", icon: <Building2 className="w-4 h-4" /> },
  { id: "high_speed", label: "High-Speed", icon: <Zap className="w-4 h-4" /> },
  { id: "scenic", label: "Scenic & Tourist", icon: <Mountain className="w-4 h-4" /> },
  { id: "international", label: "International & Sleeper", icon: <Globe2 className="w-4 h-4" /> },
  { id: "countries", label: "Country Index", icon: <Flag className="w-4 h-4" /> },
];

export default function Rail() {
  const [tab, setTab] = useState("stations");
  const [q, setQ] = useState("");
  const [overview, setOverview] = useState(null);

  useEffect(() => { api.get("/rail/overview").then((r) => setOverview(r.data)).catch(() => {}); }, []);

  return (
    <div data-testid="rail-page">
      <PageHeader eyebrow={`${SITE.name} · Rail Travel`} title="Global Rail & Train Stations"
        subtitle="Rail operators, major stations, high-speed networks, scenic journeys and international connections worldwide." entityCode="RAIL" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <GuideTeaser guide={GUIDES.rail} />
        {overview && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="rail-stats">
            {[["Stations", overview.stations], ["Operators", overview.operators], ["Routes", overview.routes],
              ["High-Speed Networks", overview.high_speed_networks], ["Countries", overview.countries]].map(([label, value]) => (
              <div key={label} className="border border-zinc-300 bg-white px-4 py-3">
                <div className="font-heading font-900 text-2xl text-emerald-700">{value}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`rail-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 border transition-colors ${tab === t.id ? "bg-emerald-700 border-emerald-800 text-white" : "bg-white border-zinc-300 text-zinc-700 hover:border-emerald-600"}`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {tab !== "countries" && (
          <div className="relative mb-6 max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="rail-search-input"
              placeholder={tab === "operators" ? "Search operators or countries…" : tab === "stations" ? "Search stations, cities or codes…" : "Search routes…"}
              className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-emerald-600" />
          </div>
        )}

        {tab === "stations" && <StationList q={q} />}
        {tab === "operators" && <OperatorList q={q} />}
        {tab === "high_speed" && <><NetworkList /><RouteList q={q} type="high_speed" title="High-speed services" /></>}
        {tab === "scenic" && <RouteList q={q} type="scenic" title="Scenic, tourist & heritage journeys" />}
        {tab === "international" && <>
          <RouteList q={q} type="international" title="International & cross-border" />
          <RouteList q={q} type="sleeper" title="Sleeper & night trains" />
          <RouteList q={q} type="airport" title="Airport rail links" />
        </>}
        {tab === "countries" && <CountryIndex />}
      </div>
    </div>
  );
}

const useFetch = (url, params) => {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params);
  useEffect(() => {
    const t = setTimeout(() => api.get(url, { params }).then((r) => setData(r.data)).catch(() => setData({ items: [] })), 250);
    return () => clearTimeout(t);
  }, [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

function StationList({ q }) {
  const data = useFetch("/rail/stations", useMemo(() => ({ q: q || undefined }), [q]));
  if (!data) return <Spinner />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rail-station-list">
      {data.items.map((s) => (
        <Link key={s.id} to={`/rail/stations/${s.id}`} data-testid={`rail-station-${s.id}`}
          className="group border border-zinc-300 bg-white p-5 hover:border-emerald-600 hover:-translate-y-px transition-[transform,border-color]">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-700 text-lg leading-snug">{s.name}</h3>
            {s.code && <span className="font-mono text-xs border border-emerald-300 bg-emerald-50 text-emerald-800 px-1.5 py-0.5">{s.code}</span>}
          </div>
          <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{s.city}, {s.country}</div>
          {s.major_destinations?.length > 0 && (
            <div className="text-xs text-zinc-600 mt-2 line-clamp-1">To: {s.major_destinations.join(" · ")}</div>
          )}
        </Link>
      ))}
      {data.items.length === 0 && <p className="text-muted-foreground text-sm col-span-full">No stations match your search yet — our rail database is expanding continuously.</p>}
    </div>
  );
}

function OperatorList({ q }) {
  const data = useFetch("/rail/operators", useMemo(() => ({ q: q || undefined }), [q]));
  if (!data) return <Spinner />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rail-operator-list">
      {data.items.map((o) => (
        <Link key={o.id} to={`/rail/operators/${o.id}`} data-testid={`rail-operator-${o.id}`}
          className="border border-zinc-300 bg-white p-5 hover:border-emerald-600 hover:-translate-y-px transition-[transform,border-color]">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading font-700 text-lg leading-snug">{o.common_name}</h3>
            <span className="text-xs font-mono uppercase border border-zinc-300 px-1.5 py-0.5 text-zinc-600">{o.type}</span>
          </div>
          <div className="text-sm text-muted-foreground mt-1">{o.country}</div>
          <div className="flex flex-wrap gap-1 mt-2">
            {(o.service_types || []).slice(0, 4).map((t) => (
              <span key={t} className="text-xs uppercase tracking-wide bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5">{t.replace("_", "-")}</span>
            ))}
          </div>
        </Link>
      ))}
    </div>
  );
}

function NetworkList() {
  const data = useFetch("/rail/networks", useMemo(() => ({}), []));
  if (!data) return <Spinner />;
  const statusTone = { operating: "bg-emerald-50 text-emerald-800 border-emerald-300", construction: "bg-amber-50 text-amber-800 border-amber-300", planned: "bg-zinc-100 text-zinc-600 border-zinc-300" };
  return (
    <div className="mb-10" data-testid="rail-network-list">
      <h2 className="font-heading font-900 text-xl mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-emerald-700" /> High-speed networks of the world</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.items.map((n) => {
          const inner = (
            <>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-700 text-lg leading-snug">{n.name}</h3>
                <span className={`text-xs font-mono uppercase border px-1.5 py-0.5 ${statusTone[n.status] || statusTone.planned}`}>{n.status}</span>
              </div>
              <div className="text-sm text-muted-foreground mt-1">{n.country}{n.opened ? ` · since ${n.opened}` : ""}{n.max_speed_kmh ? ` · up to ${n.max_speed_kmh} km/h` : ""}</div>
              <p className="text-sm text-zinc-700 mt-2">{n.description}</p>
            </>
          );
          return n.article_slug ? (
            <Link key={n.id} to={`/articles/${n.article_slug}`} data-testid={`rail-network-${n.id}`}
              className="group border border-zinc-300 bg-white p-5 hover:border-emerald-600 hover:-translate-y-px transition-[transform,border-color]">
              {inner}
              <span className="inline-flex items-center gap-1 mt-3 text-xs font-700 text-emerald-700 group-hover:underline">Read the full profile <ArrowRight className="w-3 h-3" /></span>
            </Link>
          ) : (
            <div key={n.id} className="border border-zinc-300 bg-white p-5" data-testid={`rail-network-${n.id}`}>{inner}</div>
          );
        })}
      </div>
    </div>
  );
}

export function RouteCard({ r }) {
  return (
    <div className="border border-zinc-300 bg-white p-5 hover:border-emerald-600 transition-colors" data-testid={`rail-route-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        {r.article_slug ? (
          <Link to={`/articles/${r.article_slug}`} data-testid={`rail-route-link-${r.id}`}
            className="font-heading font-700 text-lg leading-snug text-zinc-900 underline decoration-dotted underline-offset-4 decoration-emerald-600 hover:decoration-solid hover:text-emerald-700">
            {r.name}
          </Link>
        ) : (
          <h3 className="font-heading font-700 text-lg leading-snug">{r.name}</h3>
        )}
        {r.type === "sleeper" && <BedDouble className="w-4 h-4 text-emerald-700 shrink-0" />}
      </div>
      <div className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5 flex-wrap">
        <span>{r.origin}</span><ArrowRight className="w-3.5 h-3.5" /><span>{r.destination}</span>
        {r.duration && <span>· {r.duration}</span>}
        {r.max_speed_kmh && <span>· {r.max_speed_kmh} km/h</span>}
      </div>
      <p className="text-sm text-zinc-700 mt-2">{r.description}</p>
      <div className="flex items-center justify-between mt-3 text-xs">
        {r.article_slug
          ? <Link to={`/articles/${r.article_slug}`} className="text-emerald-700 font-700 hover:underline">Full guide →</Link>
          : <span className="text-muted-foreground">Last verified {r.last_verified}</span>}
        {r.booking_url && <a href={r.booking_url} target="_blank" rel="noreferrer" className="text-emerald-700 font-700 hover:underline">Official booking ↗</a>}
      </div>
    </div>
  );
}

function RouteList({ q, type, title }) {
  const data = useFetch("/rail/routes", useMemo(() => ({ q: q || undefined, type }), [q, type]));
  if (!data) return <Spinner />;
  if (data.items.length === 0) return null;
  return (
    <div className="mb-10">
      <h2 className="font-heading font-900 text-xl mb-4">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{data.items.map((r) => <RouteCard key={r.id} r={r} />)}</div>
    </div>
  );
}

function CountryIndex() {
  const data = useFetch("/rail/countries", useMemo(() => ({}), []));
  if (!data) return <Spinner />;
  return (
    <div className="border border-zinc-300 bg-white" data-testid="rail-country-index">
      <div className="grid grid-cols-4 gap-0 px-5 py-3 border-b border-zinc-200 text-xs font-mono uppercase tracking-wide text-muted-foreground">
        <span>Country</span><span className="text-right">Stations</span><span className="text-right">Operators</span><span className="text-right">Routes</span>
      </div>
      {data.items.map((c) => (
        <div key={c.country_code} className="grid grid-cols-4 px-5 py-2.5 border-b border-zinc-100 text-sm hover:bg-emerald-50/50">
          <span className="font-600">{c.country}</span>
          <span className="text-right tabular-nums">{c.stations}</span>
          <span className="text-right tabular-nums">{c.operators}</span>
          <span className="text-right tabular-nums">{c.routes}</span>
        </div>
      ))}
      <p className="px-5 py-3 text-xs text-muted-foreground flex items-center gap-1.5"><TrainFront className="w-3.5 h-3.5" /> Coverage is expanding country by country — report missing services from any station page.</p>
    </div>
  );
}
